import sessionHandling from "services/utils/notificationUtils";

// PF Configuration versions (Payroll > Configuration > Statutory > PF,
// Configuration tab). The screen keeps the pfConfigData.js shape
// ("Active", "25000", "" for empty); these mappers convert to and from the
// API shape ("ACTIVE", "25000.00", null).

const BASE = "payroll/configuration/statutory/pf/";

// 409s the PF API sends on purpose; any other 409 is the session check.
const PF_CONFLICT_CODES = ["VERSION_LOCKED", "STALE_VERSION", "IMPORT_DONE", "IMPORT_CONFLICT"];

export class PfApiError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message);
    this.name = "PfApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

const errorText = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(errorText).join(" ");
  if (value && typeof value === "object") {
    return Object.entries(value).map(([key, message]) => `${key}: ${errorText(message)}`).join(" ");
  }
  return "";
};

// { wage_ceiling: ["…"], "components.3.formula": ["…"] } -> { wage_ceiling: "…", … }
const firstMessages = (error) =>
  error && typeof error === "object" && !Array.isArray(error)
    ? Object.fromEntries(Object.entries(error).map(([key, messages]) => [key, errorText(Array.isArray(messages) ? messages[0] : messages)]))
    : undefined;

// Shared by every PF call (pfCodesApi.js and Statutory/PF/pfMembersApi.js
// too). `base` is the API prefix; `url` is a server-given path such as
// "/api/payroll/statutory/pf/members/5/form11/?company_id=1". A FormData
// body is sent as multipart (the browser sets its Content-Type);
// responseType "blob" returns { blob, fileName } for downloads.
export const request = async ({ domain, path, base = BASE, url: serverUrl, method = "GET", query, body, signal, responseType }) => {
  const url = serverUrl
    ? new URL(serverUrl, domain).toString()
    : `${domain.replace(/\/$/, "")}/${base}${path}${query ? `?${new URLSearchParams(query)}` : ""}`;
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const response = await fetch(url, {
    method,
    signal,
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      Authorization: `Token ${localStorage.getItem("token")}`,
    },
    ...(body ? { body: isForm ? body : JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new PfApiError("Your session has expired. Please sign in again.", { status: 401 });
  }
  if (responseType === "blob" && response.ok) {
    const disposition = response.headers?.get?.("Content-Disposition") || "";
    const fileName = /filename="?([^";]+)"?/i.exec(disposition)?.[1] || "";
    return { blob: await response.blob(), fileName };
  }
  const data = await response.json().catch(() => null);
  if (response.status === 409 && !PF_CONFLICT_CODES.includes(data?.code)) sessionHandling();
  if (!response.ok) {
    throw new PfApiError(errorText(data?.error || data?.detail || data) || `Request failed (${response.status}). Please try again.`, {
      status: response.status,
      code: data?.code,
      fieldErrors: firstMessages(data?.error),
    });
  }
  return data;
};

// ---- Mappers ----

const VERSION_STATUS = { ACTIVE: "Active", SUPERSEDED: "Superseded", DRAFT: "Draft" };
const COMPONENT_STATUS = { ACTIVE: "Active", INACTIVE: "Inactive" };
const KIND_ORDER = { CALCULATION: 0, CONTRIBUTION: 1 };
const AMOUNT_FIELDS = ["wage_ceiling", "eps_cap", "vpf_max_pct"];
const INT_FIELDS = ["eps_stop_age", "challan_due_day"];
const BOOL_FIELDS = ["vpf_enabled", "vpf_on_actual_allowed", "opt_out_allowed"];
// Not shown on the screen any more; always sent as false.
const OFF_FIELDS = ["intl_worker_enabled", "post_2014_rule_enabled"];
const TEXT_FIELDS = ["name", "description", "effective_from", "pf_basis_default", "rounding"];

const formText = (value) => (value == null ? "" : String(value));
const DEFAULT_CHALLAN_DUE_DAY = "15";
// "25000.00" -> "25000"; "25000.50" stays.
const formAmount = (value) => (value == null ? "" : String(value).replace(/\.0+$/, ""));
const apiValue = (value) => (value === "" || value == null ? null : value);
const apiInt = (value) => (value === "" || value == null ? null : Number(value));

export const fromApiPfVersion = (api) => ({
  id: api.id,
  name: formText(api.name),
  description: formText(api.description),
  effective_from: formText(api.effective_from),
  effective_to: formText(api.effective_to),
  status: VERSION_STATUS[api.status] || "",
  ...Object.fromEntries(AMOUNT_FIELDS.map((key) => [key, formAmount(api[key])])),
  ...Object.fromEntries(INT_FIELDS.map((key) => [key, formText(api[key])])),
  // Due day defaults to the 15th when the server has none.
  challan_due_day: formText(api.challan_due_day) || DEFAULT_CHALLAN_DUE_DAY,
  ...Object.fromEntries(BOOL_FIELDS.map((key) => [key, Boolean(api[key])])),
  pf_basis_default: formText(api.pf_basis_default),
  rounding: formText(api.rounding),
  components: [...(api.components || [])]
    .sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9) || a.sequence - b.sequence)
    .map((component) => ({
      code: component.code,
      name: component.name,
      kind: component.kind,
      formula: component.formula,
      used_for: formText(component.used_for),
      challan_account: component.challan_account,
      status: COMPONENT_STATUS[component.status] || component.status,
      fixed: Boolean(component.fixed),
    })),
  is_locked: Boolean(api.is_locked),
  used_by: api.used_by || [],
  source_version_id: api.source_version_id ?? null,
  row_version: api.row_version ?? null,
  created_by: formText(api.created_by),
  created_at: formText(api.created_at),
  updated_by: formText(api.updated_by),
  updated_at: formText(api.updated_at),
});

// The body shared by POST and PUT. Leaves out what the server sets (id,
// status, effective_to, is_locked, used_by, audit fields).
export const toApiPfVersion = (form, companyId) => {
  const counters = {};
  return {
    company_id: Number(companyId),
    ...Object.fromEntries(TEXT_FIELDS.map((key) => [key, key === "description" ? form[key] || "" : apiValue(form[key])])),
    ...Object.fromEntries(AMOUNT_FIELDS.map((key) => [key, apiValue(form[key])])),
    ...Object.fromEntries(INT_FIELDS.map((key) => [key, apiInt(form[key])])),
    ...Object.fromEntries(BOOL_FIELDS.map((key) => [key, Boolean(form[key])])),
    ...Object.fromEntries(OFF_FIELDS.map((key) => [key, false])),
    components: form.components.map((component) => {
      counters[component.kind] = (counters[component.kind] || 0) + 1;
      return {
        code: component.code,
        name: component.name,
        kind: component.kind,
        formula: component.formula,
        used_for: component.used_for || "",
        challan_account: component.challan_account || "none",
        status: String(component.status || "").toUpperCase(),
        fixed: Boolean(component.fixed),
        sequence: counters[component.kind],
      };
    }),
  };
};

// ---- Requests ----

export const fetchPfVersions = async ({ domain, companyId, signal }) => {
  const data = await request({ domain, path: "versions/", query: { company_id: companyId }, signal });
  if (!Array.isArray(data)) throw new PfApiError("The server returned an invalid PF configuration.");
  return data.map(fromApiPfVersion);
};

export const fetchPfDefaults = async ({ domain, companyId, signal }) =>
  fromApiPfVersion(await request({ domain, path: "versions/defaults/", query: { company_id: companyId }, signal }));

const saved = (data) => ({ version: fromApiPfVersion(data.data), message: data.message });

// Save & Activate a draft.
export const createPfVersion = async ({ domain, companyId, form }) =>
  saved(
    await request({
      domain,
      path: "versions/",
      method: "POST",
      body: { ...toApiPfVersion(form, companyId), source_version_id: form.source_version_id ?? null },
    }),
  );

// Save a saved, unlocked version.
export const updatePfVersion = async ({ domain, companyId, form }) =>
  saved(
    await request({
      domain,
      path: `versions/${form.id}/`,
      method: "PUT",
      body: { ...toApiPfVersion(form, companyId), row_version: form.row_version },
    }),
  );
